import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isValid } from 'date-fns';
import {
    saveToStorage,
    getFromStorage,
    clearStorage,
} from '@forvalter/utils/storage';
import { localStorageReferences } from '../utils';

export interface IKommentarerState {
    selectedDate: Date;
    pinKommentarerMenu: boolean;
    editMode: {
        editFlag: boolean;
        kommentarId: string;
    };
    postMode: boolean;
    openModal: boolean;
    highlightedCommentsToRemove: Array<string>;
}

export interface IKommentarerStore {
    kommentarer: IKommentarerState;
}
const saveSelectedDateToStorage = createAsyncThunk(
    'kommentarer/saveSelectedDateToStorage',
    async (date: Date) => {
        saveToStorage(localStorageReferences.SELECTED_DATE, date);
        return date;
    },
);

const savePinCommentMenuToStorage = createAsyncThunk(
    'kommentarer/savePinCommentToStorage',
    async (pin: boolean) => {
        saveToStorage(localStorageReferences.PINNED_COMMENT_MENU, pin);
        return pin;
    },
);

const saveHighlightedCommentsToRemoveToStorage = createAsyncThunk(
    'kommentarer/saveHighlightedCommentsToRemoveToStorage',
    async (highlightedCommentsToRemove: Array<string>) => {
        saveToStorage(
            localStorageReferences.HIGHLIGHTED_COMMENTS_TO_REMOVE,
            highlightedCommentsToRemove,
        );
        return highlightedCommentsToRemove;
    },
);

const clearHighlightedCommentsToRemoveFromStorage = createAsyncThunk(
    'kommentarer/clearHighlightedCommentsToRemoveFromStorage',
    async () => {
        try {
            clearStorage(localStorageReferences.HIGHLIGHTED_COMMENTS_TO_REMOVE);
            return [];
        } catch (error) {
            return [];
        }
    },
);

const storedDate = getFromStorage(localStorageReferences.SELECTED_DATE);
const parsedDate = storedDate ? new Date(storedDate as string) : new Date();
const selectedDateFromStorage = isValid(parsedDate) ? parsedDate : new Date();

const pinCommentFromStorage = getFromStorage(
    localStorageReferences.PINNED_COMMENT_MENU,
) as boolean;

const highlightedCommentsToRemoveFromStorage =
    (getFromStorage(
        localStorageReferences.HIGHLIGHTED_COMMENTS_TO_REMOVE,
    ) as Array<string>) || [];

export const kommentarerSelectorSlice = createSlice({
    name: 'kommentarerSelector',
    initialState: {
        selectedDate: selectedDateFromStorage,
        pinKommentarerMenu: pinCommentFromStorage,
        editMode: { editFlag: false, kommentarId: '' },
        postMode: false,
        openModal: false,
        highlightedCommentsToRemove: highlightedCommentsToRemoveFromStorage,
    } satisfies IKommentarerState,
    reducers: {
        setEditMode: (state, action) => {
            state.editMode = action.payload;
        },
        setOpenModal: (state, action) => {
            state.openModal = action.payload;
        },
        setPostMode: (state, action) => {
            state.postMode = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(saveSelectedDateToStorage.fulfilled, (state, action) => {
                state.selectedDate = action.payload;
            })
            .addCase(savePinCommentMenuToStorage.fulfilled, (state, action) => {
                state.pinKommentarerMenu = action.payload;
            })
            .addCase(savePinCommentMenuToStorage.rejected, (state) => {
                state.pinKommentarerMenu = false;
            })
            .addCase(
                saveHighlightedCommentsToRemoveToStorage.fulfilled,
                (state, action) => {
                    state.highlightedCommentsToRemove = action.payload;
                },
            )
            .addCase(
                saveHighlightedCommentsToRemoveToStorage.rejected,
                (state) => {
                    state.highlightedCommentsToRemove = [];
                },
            )
            .addCase(
                clearHighlightedCommentsToRemoveFromStorage.fulfilled,
                (state) => {
                    state.highlightedCommentsToRemove = [];
                },
            )
            .addCase(
                clearHighlightedCommentsToRemoveFromStorage.rejected,
                (state) => {
                    state.highlightedCommentsToRemove = [];
                },
            );
    },
});

export const kommentarerSelectors = ({
    kommentarer,
}: IKommentarerStore): IKommentarerState => kommentarer;

export const kommentarerSelectorActions = {
    savePinCommentMenuToStorage,
    saveHighlightedCommentsToRemoveToStorage,
    clearHighlightedCommentsToRemoveFromStorage,
    saveSelectedDateToStorage,
    reducerActions: kommentarerSelectorSlice.actions,
};
